import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useGetTickerOptionFlowLazyQuery,
  useGetFreeTickerOptionFlowLazyQuery,
} from 'types/generated/client';
import { hasDashboardAccess } from 'services/client/token';
import { AuthStatus } from 'constants/auth';
import { isMarketHours } from 'utils/shared/time/marketHours';
import getDerivedTradeValues from 'utils/shared/trades/getDerivedTradeValues';
import { Trade } from 'utils/shared/trades/types';
import type { FlowSideBasisSelection } from 'utils/shared/trades/presets';
import { getDefaultFlowSideBasisSelection } from 'utils/shared/trades/presets';
import { filterTradesByFlowSideBasis } from 'utils/shared/trades/aggregateOptionFlow';
import { getTradeSidesForFlowSideBasis } from 'utils/shared/trades/flowSideBasis';
import { getTickerFlowSinceDate } from 'utils/shared/trades/tickerFlowWindow';
import { Viewer } from 'constants/user';

const POLL_INTERVAL = 2 * 60_000;
const DEFAULT_FLOW_SIDE_BASIS = getDefaultFlowSideBasisSelection();

interface UseTickerFlowDataReturn {
  trades: Trade[];
  isLoading: boolean;
  shouldHaveFree: boolean;
  shouldHavePaid: boolean;
  refetch: () => void;
}

export default function useTickerFlowData(
  ticker: string,
  viewer: Viewer,
  flowSideBasis: FlowSideBasisSelection = DEFAULT_FLOW_SIDE_BASIS,
): UseTickerFlowDataReturn {
  const isLoadingAuth = viewer.status === AuthStatus.Loading;
  const shouldHavePaid = viewer.status === AuthStatus.User && hasDashboardAccess(viewer);
  const shouldHaveFree =
    viewer.status === AuthStatus.Anonymous ||
    (viewer.status === AuthStatus.User && !hasDashboardAccess(viewer));

  const [
    getTickerOptionFlowQuery,
    { data: paidData, refetch: paidRefetch, loading: paidLoading, called: paidCalled },
  ] = useGetTickerOptionFlowLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [
    getFreeTickerOptionFlowQuery,
    { data: freeData, refetch: freeRefetch, loading: freeLoading, called: freeCalled },
  ] = useGetFreeTickerOptionFlowLazyQuery({
    fetchPolicy: 'no-cache',
  });

  // Store latest refs so the effect and interval always use current values
  // without needing them in the dependency array
  const paidRefetchRef = useRef(paidRefetch);
  const freeRefetchRef = useRef(freeRefetch);
  paidRefetchRef.current = paidRefetch;
  freeRefetchRef.current = freeRefetch;
  const tradeSides = useMemo(() => getTradeSidesForFlowSideBasis(flowSideBasis), [flowSideBasis]);
  // PERF: literal rolling date bound so the Latest views use the date index. See
  // utils/shared/trades/tickerFlowWindow. Stable within a day, so deps stay quiet.
  const sinceDate = useMemo(() => getTickerFlowSinceDate(), []);

  const fetchData = useCallback(() => {
    if (isLoadingAuth || !ticker) return;
    const upperTicker = ticker.toUpperCase();

    if (shouldHavePaid) {
      if (paidRefetchRef.current) {
        paidRefetchRef.current({ ticker: upperTicker, tradeSides, sinceDate });
      } else {
        getTickerOptionFlowQuery({ variables: { ticker: upperTicker, tradeSides, sinceDate } });
      }
    } else if (shouldHaveFree) {
      if (freeRefetchRef.current) {
        freeRefetchRef.current({ ticker: upperTicker, sinceDate });
      } else {
        getFreeTickerOptionFlowQuery({ variables: { ticker: upperTicker, sinceDate } });
      }
    }
  }, [
    ticker,
    isLoadingAuth,
    shouldHavePaid,
    shouldHaveFree,
    getTickerOptionFlowQuery,
    getFreeTickerOptionFlowQuery,
    tradeSides,
    sinceDate,
  ]);

  useEffect(() => {
    if (isLoadingAuth || !ticker) return;

    fetchData();

    // Auto-poll for paid users only. Free/anonymous users refresh manually.
    if (!shouldHavePaid) return;

    // @ts-ignore issue with Node / browser types
    const interval: number = setInterval(() => {
      if (isMarketHours() && paidRefetchRef.current) {
        paidRefetchRef.current({ ticker: ticker.toUpperCase(), tradeSides, sinceDate });
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [ticker, isLoadingAuth, shouldHavePaid, fetchData, tradeSides, sinceDate]);

  const isLoading =
    isLoadingAuth || (shouldHavePaid ? paidLoading || !paidCalled : freeLoading || !freeCalled);

  const trades = useMemo(() => {
    const rawTrades = shouldHavePaid
      ? paidData?.ticker_option_flow || []
      : freeData?.free_ticker_option_flow || [];

    const processedTrades = rawTrades.map((trade) => {
      const spot = trade.spot || trade.priceExternalApi;
      return getDerivedTradeValues({ ...trade, spot }, false);
    });

    return filterTradesByFlowSideBasis(processedTrades, flowSideBasis);
  }, [paidData, freeData, shouldHavePaid, flowSideBasis]);

  return { trades, isLoading, shouldHaveFree, shouldHavePaid, refetch: fetchData };
}
